/**
 * Die REGELN des Vier-Augen-Prinzips beim Loeschen eines Projekts — ohne
 * Supabase, ohne DOM, ohne Uhr aus der Umgebung.
 *
 * Die Wahrheit steht in der Datenbank (`supabase/project_deletion_approvals.sql`,
 * MIGRATIONS.md Nr. 49): dort sitzen die CHECKs, die RESTRICTIVE-Policy und der
 * BEFORE-DELETE-Trigger. Was hier steht, ist die VORHERSAGE derselben Regeln
 * fuer die Oberflaeche — damit ein Knopf gar nicht erst anklickbar ist, dessen
 * Aufruf der Server ohnehin abweisen wuerde.
 *
 * Genau deshalb darf hier NIE eine Regel weicher sein als im SQL. Wird dort
 * etwas geaendert, gehoert es hier mit geaendert; umgekehrt schuetzt der
 * Server weiterhin, wenn hier jemand nachlaessig ist.
 */

export type DeletionRequestStatus =
    | 'pending'
    | 'approved'
    | 'rejected'
    | 'cancelled'
    | 'executed';

export interface ProjectDeletionRequest {
    id: string;
    project_id: string;
    project_name: string;
    project_number?: string | null;
    project_owner_id?: string | null;
    company_id?: string | null;
    company_name?: string | null;
    requested_by: string;
    requested_by_name: string;
    reason: string;
    approver_id: string;
    approver_name: string;
    status: DeletionRequestStatus;
    decided_by?: string | null;
    decided_by_name?: string | null;
    decided_at?: string | null;
    decision_note?: string | null;
    executed_at?: string | null;
    executed_by?: string | null;
    created_at: string;
    expires_at: string;
}

/**
 * Dieselbe Zahl wie im CHECK `pdr_reason_chk`. Unterhalb davon stehen nur
 * „weg", „Test" und „falsch angelegt" — Begruendungen, die in einem halben
 * Jahr niemandem mehr sagen, warum eine Baustelle fehlt.
 */
export const MIN_REASON_LENGTH = 20;

/** Rollen, die beantragen und pruefen duerfen. Deckungsgleich mit `is_admin()`. */
const ADMIN_ROLES = ['admin', 'super_admin'];

export interface DeletionActor {
    id?: string | null;
    role?: string | null;
}

export const istLoeschAdmin = (user?: DeletionActor | null): boolean =>
    !!user?.id && ADMIN_ROLES.includes(user?.role || '');

/**
 * Prueft die Begruendung und liefert den Klartext des Mangels — oder `null`,
 * wenn sie taugt. Getrimmt geprueft, genau wie der CHECK: zwanzig Leerzeichen
 * sind keine Begruendung.
 */
export const pruefeBegruendung = (reason: string): string | null => {
    const trimmed = (reason || '').trim();
    if (trimmed.length === 0) return 'Bitte begründen Sie die Löschung.';
    if (trimmed.length < MIN_REASON_LENGTH) {
        return `Noch ${MIN_REASON_LENGTH - trimmed.length} Zeichen — bitte benennen Sie, WARUM das Projekt weg soll.`;
    }
    return null;
};

/** Ein Antrag ist abgelaufen, sobald `expires_at` vorbei ist. */
export const istAbgelaufen = (
    request: Pick<ProjectDeletionRequest, 'expires_at'>,
    jetzt: Date = new Date(),
): boolean => {
    const ende = Date.parse(request.expires_at);
    if (Number.isNaN(ende)) return false;
    return ende < jetzt.getTime();
};

/** Offen heisst: wartet noch auf Pruefung oder auf die Ausfuehrung. */
export const istOffen = (request: Pick<ProjectDeletionRequest, 'status'>): boolean =>
    request.status === 'pending' || request.status === 'approved';

/**
 * Offen UND noch nicht abgelaufen — das, was die Liste „Nur offene" und der
 * Zaehler zeigen. Ein abgelaufener Antrag verlangt von niemandem mehr etwas;
 * die Datenbank schliesst ihn beim naechsten Laden ohnehin
 * (`close_expired_project_deletion_requests`, MIGRATIONS.md Nr. 159).
 */
export const istNochOffen = (
    request: Pick<ProjectDeletionRequest, 'status' | 'expires_at'>,
    jetzt: Date = new Date(),
): boolean => istOffen(request) && !istAbgelaufen(request, jetzt);

/**
 * Anfang der Notiz, die `close_expired_project_deletion_requests` (Nr. 159)
 * einem abgelaufenen Antrag schreibt. Der Status ist danach `cancelled`; nur
 * an diesem Anfang erkennt die Anzeige, dass niemand zurueckgezogen hat.
 * Muss mit dem SQL uebereinstimmen.
 */
export const ABLAUF_PRAEFIX = 'Abgelaufen am ';

export const istAutomatischGeschlossen = (
    request: Pick<ProjectDeletionRequest, 'status' | 'decision_note'>,
): boolean => request.status === 'cancelled' && (request.decision_note || '').startsWith(ABLAUF_PRAEFIX);

/**
 * Darf dieser Mensch ueber diesen Antrag entscheiden?
 *
 * NUR der ausgeloste Pruefer — kein „super_admin darf auch". Genau diese
 * Ausnahme hoehlte das Prinzip wieder aus, weil der Antragsteller hier oft
 * selbst super_admin ist. Der Selbst-Ausschluss steht zusaetzlich da, obwohl
 * die Auslosung ihn schon ausschliesst: eine Bedingung, die den Kern der Sache
 * traegt, soll man lesen koennen, ohne der Auslosung zu vertrauen.
 */
export const darfEntscheiden = (
    request: ProjectDeletionRequest,
    user?: DeletionActor | null,
    jetzt: Date = new Date(),
): boolean =>
    istLoeschAdmin(user)
    && request.status === 'pending'
    && request.approver_id === user?.id
    && request.requested_by !== user?.id
    && !istAbgelaufen(request, jetzt);

/**
 * Darf dieser Mensch die freigegebene Loeschung ausfuehren?
 *
 * Antragsteller ODER Pruefer — die zwei Augenpaare liegen zu diesem Zeitpunkt
 * bereits vor. `decided_by === requested_by` kann es nach dem CHECK gar nicht
 * geben; die Bedingung steht trotzdem hier, damit ein von Hand verbogener
 * Datensatz keinen Knopf freischaltet.
 */
export const darfAusfuehren = (
    request: ProjectDeletionRequest,
    user?: DeletionActor | null,
    jetzt: Date = new Date(),
): boolean =>
    istLoeschAdmin(user)
    && request.status === 'approved'
    && !!request.decided_by
    && request.decided_by !== request.requested_by
    && (request.requested_by === user?.id || request.approver_id === user?.id)
    && !istAbgelaufen(request, jetzt);

/**
 * Zuruecknehmen darf der Antragsteller — auch eine bereits erteilte Freigabe,
 * solange nicht geloescht ist. Der haeufigste Fall ist Reue, nicht
 * Boeswilligkeit. Ein super_admin darf ebenfalls, damit ein Antrag eines
 * ausgeschiedenen Kollegen nicht ewig steht.
 */
export const darfZuruecknehmen = (
    request: ProjectDeletionRequest,
    user?: DeletionActor | null,
): boolean =>
    istOffen(request)
    && (request.requested_by === user?.id || user?.role === 'super_admin');

export interface StatusAnzeige {
    text: string;
    /** Tailwind-Klassen fuer die Plakette. */
    ton: string;
}

export const beschreibeStatus = (
    request: ProjectDeletionRequest,
    jetzt: Date = new Date(),
): StatusAnzeige => {
    if (request.status === 'pending' && istAbgelaufen(request, jetzt)) {
        return { text: 'Abgelaufen', ton: 'bg-slate-100 text-slate-500 border-slate-200' };
    }
    if (request.status === 'approved' && istAbgelaufen(request, jetzt)) {
        return { text: 'Freigabe abgelaufen', ton: 'bg-slate-100 text-slate-500 border-slate-200' };
    }
    if (istAutomatischGeschlossen(request)) {
        return { text: 'Abgelaufen', ton: 'bg-slate-100 text-slate-500 border-slate-200' };
    }
    switch (request.status) {
        case 'pending':
            return { text: 'Wartet auf Freigabe', ton: 'bg-amber-50 text-amber-700 border-amber-200' };
        case 'approved':
            return { text: 'Freigegeben — noch nicht gelöscht', ton: 'bg-blue-50 text-blue-700 border-blue-200' };
        case 'rejected':
            return { text: 'Abgelehnt', ton: 'bg-rose-50 text-rose-700 border-rose-200' };
        case 'cancelled':
            return { text: 'Zurückgezogen', ton: 'bg-slate-100 text-slate-600 border-slate-200' };
        case 'executed':
            return { text: 'Gelöscht', ton: 'bg-slate-800 text-white border-slate-800' };
        default:
            return { text: request.status, ton: 'bg-slate-100 text-slate-600 border-slate-200' };
    }
};

/**
 * Sortierung der Liste: was von MIR eine Handlung verlangt, steht oben. Danach
 * alles Offene, danach das Erledigte — je Gruppe das Juengste zuerst.
 */
export const sortiereAntraege = (
    requests: ProjectDeletionRequest[],
    user?: DeletionActor | null,
    jetzt: Date = new Date(),
): ProjectDeletionRequest[] => {
    const rang = (r: ProjectDeletionRequest): number => {
        if (darfEntscheiden(r, user, jetzt)) return 0;
        if (darfAusfuehren(r, user, jetzt)) return 1;
        if (istNochOffen(r, jetzt)) return 2;
        return 3;
    };
    return [...requests].sort((a, b) => {
        const d = rang(a) - rang(b);
        if (d !== 0) return d;
        return Date.parse(b.created_at) - Date.parse(a.created_at);
    });
};

/**
 * Die Antraege, die DIESER Nutzer jetzt entscheiden muss — fuer das
 * Pflicht-Popup. Der aelteste zuerst: er laeuft am fruehesten ab.
 */
export const meinePruefungen = (
    requests: ProjectDeletionRequest[],
    user?: DeletionActor | null,
    jetzt: Date = new Date(),
): ProjectDeletionRequest[] =>
    requests
        .filter((r) => darfEntscheiden(r, user, jetzt))
        .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));

/** Anzahl der Antraege, die auf die Entscheidung DIESES Nutzers warten. */
export const zaehleMeineAufgaben = (
    requests: ProjectDeletionRequest[],
    user?: DeletionActor | null,
    jetzt: Date = new Date(),
): number => requests.filter((r) => darfEntscheiden(r, user, jetzt) || darfAusfuehren(r, user, jetzt)).length;
